import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Link as LinkIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { 
  getLinkedAccounts, 
  linkAccount, 
  unlinkAccount, 
  sendVerificationEmail,
  verifyEmail 
} from '../services/api';

interface LinkedAccount {
  id: string;
  email: string;
  is_verified: boolean;
  linked_at: string;
}

const AccountLinkingPage: React.FC = () => {
  const { user } = useAuth();
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState<string | null>(null);
  const [unlinkDialog, setUnlinkDialog] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadLinkedAccounts();
  }, []);

  const loadLinkedAccounts = async () => {
    try {
      setLoading(true);
      const accounts = await getLinkedAccounts();
      setLinkedAccounts(accounts);
    } catch (err) {
      setError('Failed to load linked accounts');
      console.error('Error loading linked accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkAccount = async () => {
    if (!newEmail.trim()) return;

    try {
      setLinkLoading(true);
      setError(null);
      await linkAccount(newEmail);
      setNewEmail('');
      setSuccess('Account linking initiated. Please check your email for verification.');
      await loadLinkedAccounts();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to link account');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleSendVerification = async (accountId: string) => {
    try {
      setVerificationLoading(accountId);
      setError(null);
      await sendVerificationEmail(accountId);
      setSuccess('Verification email sent successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send verification email');
    } finally {
      setVerificationLoading(null);
    }
  };

  const handleUnlinkAccount = async (accountId: string) => {
    try {
      setError(null);
      await unlinkAccount(accountId);
      setSuccess('Account unlinked successfully');
      await loadLinkedAccounts();
      setUnlinkDialog(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to unlink account');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Account Linking
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Link additional email accounts to share compasses and receive notifications across multiple accounts.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Primary Account */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Primary Account
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <EmailIcon color="primary" />
            <Typography variant="body1">{user?.email}</Typography>
            <Chip label="Primary" color="primary" size="small" />
            <Chip label="Verified" color="success" size="small" icon={<CheckCircleIcon />} />
          </Box>
        </CardContent>
      </Card>

      {/* Link New Account */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Link New Account
          </Typography>
          <Box display="flex" gap={2} alignItems="flex-start">
            <TextField
              label="Email Address"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter email to link"
              fullWidth
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleLinkAccount}
              disabled={!newEmail.trim() || linkLoading}
              startIcon={linkLoading ? <CircularProgress size={16} /> : <LinkIcon />}
            >
              Link Account
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            A verification email will be sent to the email address you want to link.
          </Typography>
        </CardContent>
      </Card>

      {/* Linked Accounts */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Linked Accounts ({linkedAccounts.length})
          </Typography>
          
          {linkedAccounts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No additional accounts linked. Link an account above to share compasses across multiple email addresses.
            </Typography>
          ) : (
            <List>
              {linkedAccounts.map((account, index) => (
                <React.Fragment key={account.id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <Box display="flex" alignItems="center" gap={2} flex={1}>
                      <EmailIcon color="action" />
                      <ListItemText
                        primary={account.email}
                        secondary={`Linked on ${new Date(account.linked_at).toLocaleDateString()}`}
                      />
                      {account.is_verified ? (
                        <Chip label="Verified" color="success" size="small" icon={<CheckCircleIcon />} />
                      ) : (
                        <Chip label="Unverified" color="warning" size="small" icon={<WarningIcon />} />
                      )}
                    </Box>
                    <ListItemSecondaryAction>
                      <Box display="flex" gap={1}>
                        {!account.is_verified && (
                          <Button
                            size="small"
                            onClick={() => handleSendVerification(account.id)}
                            disabled={verificationLoading === account.id}
                            startIcon={verificationLoading === account.id ? 
                              <CircularProgress size={16} /> : <EmailIcon />}
                          >
                            Verify
                          </Button>
                        )}
                        <IconButton
                          onClick={() => setUnlinkDialog(account.id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Unlink Confirmation Dialog */}
      <Dialog
        open={!!unlinkDialog}
        onClose={() => setUnlinkDialog(null)}
      >
        <DialogTitle>Unlink Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to unlink this account? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinkDialog(null)}>Cancel</Button>
          <Button
            onClick={() => unlinkDialog && handleUnlinkAccount(unlinkDialog)}
            color="error"
            variant="contained"
          >
            Unlink
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AccountLinkingPage;
